/**
 * Trunnion AI - Agent List View
 * Paginated agent list.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus } from 'lucide-react';
import { AgentCard } from './AgentCard';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { useAgentList } from '@/hooks/queries/useAgents';
import { ROUTES } from '@/config/routes';

export function AgentListView() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useAgentList({
    search,
    tier: tierFilter || undefined,
    page,
    pageSize: 12,
  });

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-trunnion-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search agents..."
            className="w-full rounded-lg border border-trunnion-slate-600 bg-trunnion-slate-800 py-2 pl-9 pr-3 text-sm text-white placeholder-trunnion-slate-400 focus:outline-none focus:ring-2 focus:ring-trunnion-gold-500"
          />
        </div>
        <Select
          options={[
            { value: '', label: 'All Tiers' },
            { value: 'brain', label: 'Brain' },
            { value: 'execution_proxy', label: 'Execution Proxy' },
            { value: 'console_automation', label: 'Console Automation' },
          ]}
          value={tierFilter}
          onChange={(e) => setTierFilter(e.target.value)}
        />
        <Button
          variant="primary"
          icon={<Plus className="h-4 w-4" />}
          onClick={() => navigate(ROUTES.AGENT_NEW)}
        >
          New Agent
        </Button>
      </div>

      {/* Agent Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <LoadingSpinner size="lg" label="Loading agents..." />
        </div>
      ) : data?.agents.length === 0 ? (
        <EmptyState
          message="No agents found"
          description="Create your first AI agent to get started."
          action={
            <Button
              icon={<Plus className="h-4 w-4" />}
              onClick={() => navigate(ROUTES.AGENT_NEW)}
            >
              Create Agent
            </Button>
          }
        />
      ) : (
        <>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {data?.agents.map((agent) => (
              <AgentCard
                key={agent.id}
                agent={agent}
                onClick={() => navigate(ROUTES.AGENT_DETAIL(agent.id))}
              />
            ))}
          </div>
          {data && data.total > 12 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPage(page - 1)}
                disabled={page <= 1}
              >
                Previous
              </Button>
              <span className="text-sm text-trunnion-slate-400">
                Page {page} of {Math.ceil(data.total / 12)}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPage(page + 1)}
                disabled={page >= Math.ceil(data.total / 12)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
